// Centralised SEO copy builders. Everything here must be derived from real
// catalog data or the site's published policies — never invented claims.

import { parseTitleAttributes } from "@/lib/productTitle";

interface MinimalNode {
  node: { title: string; productType?: string | null };
}

const topValues = (values: (string | null | undefined)[], limit: number): string[] => {
  const counts = new Map<string, number>();
  for (const v of values) {
    if (!v) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([v]) => v);
};

const list = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  return items.slice(0, -1).join(", ") + " and " + items[items.length - 1];
};


/** Facts we can honestly state about a category, parsed from its products. */
export interface CategoryFacts {
  materials: string[];
  configs: string[];
  types: string[];
  count: number;
}


/**
 * A material is "most common" only when it is common: three or more of the
 * pieces shown and a fifth of them. The top three of any count named "teak,
 * rubber and linen" for Rugs and "cotton and bamboo" for mattresses that are
 * mostly memory foam. A word another kept value contains goes ("wood" beside
 * "solid wood").
 */
const commonMaterials = (values: (string | null)[], shown: number): string[] => {
  const counts = new Map<string, number>();
  for (const v of values) if (v) counts.set(v, (counts.get(v) || 0) + 1);
  const kept = [...counts.entries()]
    .filter(([, n]) => n >= 3 && n / shown >= 0.2)
    .sort((a, b) => b[1] - a[1])
    .map(([v]) => v);
  return kept
    .filter((m) => !kept.some((o) => o !== m && o.toLowerCase().split(" ").includes(m.toLowerCase())))
    .slice(0, 3);
};

const COLOUR_LIKE_MATERIAL = /^(copper|bronze|gold|silver|pewter|nickel|brass|chrome|ivory|ebony)$/i;

export function extractCategoryFacts<T extends MinimalNode>(
  products: T[],
  totalCount: number | null
): CategoryFacts {
  const materials: (string | null)[] = [];
  const configs: (string | null)[] = [];
  const types: (string | null | undefined)[] = [];
  for (const p of products) {
    const a = parseTitleAttributes(p.node.title);
    // Metal and ivory words double as colours and finishes ("Copper And Orange …
    // Rug", "Antique Bronze" lanterns): stating them as materials was false.
    materials.push(a.material && !COLOUR_LIKE_MATERIAL.test(a.material) ? a.material : null);
    configs.push(a.configs[0] || null);
    types.push(p.node.productType || a.type);
  }
  return {
    materials: commonMaterials(materials, products.length),
    configs: topValues(configs, 2),
    types: topValues(types, 3),
    count: totalCount ?? products.length,
  };
}


const SEATING_TYPES = /^(Sofas|Sectionals|Love Seats|Seating Sets|Sleeper Sofas|Futons)$/i;

/** Lower-case a catalogue phrase for use mid-sentence, keeping proper adjectives. */
const phrase = (s: string) => s.toLowerCase().replace(/\bitalian\b/g, "Italian").replace(/\bpersian\b/g, "Persian");

/**
 * 2–3 sentence intro paragraph shown under the category H1. States only the
 * build-time in-stock count and what the loaded listings show.
 */
export function categoryIntro(
  categoryName: string,
  description: string | undefined,
  facts: CategoryFacts
): string {
  const sentences: string[] = [];
  // The count line right above the intro already states how many are in stock.
  // The description always leads: it is what the prerendered page shows, so the
  // text no longer changes when the products arrive.
  if (description) sentences.push(description.trim().replace(/([^.!?])$/, "$1."));
  if (facts.materials.length) {
    sentences.push(
      facts.materials.length === 1
        ? `Among the pieces shown, the most common material is ${phrase(facts.materials[0])}.`
        : `Among the pieces shown, the most common materials are ${list(facts.materials.map(phrase))}.`
    );
  }
  // Corner, curved, chaise and modular are seating shapes: in a bedroom or
  // dining room they described a few stray titles, not a way to shop the room.
  // Two shapes among the pieces shown is also when the Configuration filter is
  // offered. No other filter is named: 82 of 119 pages were pointed to a Size
  // filter they do not have.
  const seating = facts.types.some((t) => SEATING_TYPES.test(t));
  sentences.push(
    facts.configs.length > 1 && seating
      ? `Measure your space first, then filter by ${list(facts.configs.map(phrase))} configuration to narrow things down.`
      : `Measure your space first, then use the filters to narrow things down.`
  );
  return sentences.join(" ");
}
